import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { Spinner } from "../../components/ui/misc";
import { api } from "../../lib/api/endpoints";
import type { Revision } from "../../lib/api/models";
import { formatDateTime } from "../../lib/format";
import { qk } from "../../lib/queries";

const SOURCE: Record<Revision["source"], string> = { ui: "Editor", api: "API", import: "Import", restore: "Restore" };

export function HistoryPanel({
  docId,
  selected,
  onSelect,
  disabled = false,
}: {
  docId: string;
  selected: number | null;
  onSelect: (version: number | null) => void;
  /** While editing: a version would only open after Done, so none can be chosen. */
  disabled?: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: qk.revisions(docId),
    queryFn: () => api.revisions.list(docId),
  });
  if (isLoading) return <Spinner label="Loading history…" />;
  if (error) return <p className="text-sm text-danger">History could not be loaded: {error.message}</p>;
  return (
    <>
      {disabled ? <p className="mb-2 text-sm text-ink-2">Finish editing to view versions.</p> : null}
      <ol className="grid gap-1">
        {(data ?? []).map((r) => (
          <li key={r.version}>
            <button
              type="button"
              disabled={disabled}
              aria-pressed={selected === r.version}
              onClick={() => onSelect(selected === r.version ? null : r.version)}
              className={clsx(
                "grid w-full gap-0.5 rounded-md px-2.5 py-2 text-left text-sm",
                selected === r.version ? "bg-green-wash" : "enabled:hover:bg-desk",
                "disabled:opacity-60",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink">Version {r.version}</span>
                <span className="rounded border border-line px-1.5 text-2xs text-ink-2">{SOURCE[r.source]}</span>
              </span>
              <span className="text-ink-2">
                {r.author?.name ?? "Unknown"} · {formatDateTime(r.createdAt)}
              </span>
              {r.note ? <span className="text-ink-3 italic">{r.note}</span> : null}
            </button>
          </li>
        ))}
      </ol>
    </>
  );
}
